import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { localTimeZoneAtom } from 'atoms/tournament';
import { getClientConfig } from 'config/clients';
import { formatDateTime } from 'utils/dateTimeUtils';

type UseDateTimeFormatBaseOptions = {
  zone?: 'local' | 'tournament' | Intl.DateTimeFormatOptions['timeZone'];
  locale?: 'local' | 'client';
  invalid?: Parameters<typeof formatDateTime>[4];
};

export type UseDateTimeFormatOptions = UseDateTimeFormatBaseOptions &
  ({ format: Parameters<typeof formatDateTime>[1] } | Intl.DateTimeFormatOptions);

/**
 *
 * @param value An ISO 8601 string, Date or Dayjs instance
 * @param options A list of `Intl.DateTimeFormat` options. Also:
 * @param options.format An optional manual format to use. Provide a key from the client config
 * @param options.zone Either an exact timezone to use or the `local` browsers tz or the `tournament` tz
 * @param options.locale Prefer the `local` browser locale or the locale from the `client` config
 * @param options.invalid String to use when return value is invalid
 * @returns A formatted date and time
 */
const useDateTimeFormat = (value: Parameters<typeof formatDateTime>[0], options: UseDateTimeFormatOptions): string => {
  const { zone = 'tournament', locale = 'client', invalid, ...remainingOptions } = options;
  const format = 'format' in remainingOptions ? remainingOptions.format : remainingOptions;
  const { t } = useTranslation();
  const tournamentTimeZone = useAtomValue(localTimeZoneAtom);
  let timeZoneValue;
  switch (zone) {
    case 'tournament':
      timeZoneValue = tournamentTimeZone;
      break;
    case 'local':
      timeZoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone;
      break;
    default:
      timeZoneValue = zone ?? 'UTC';
  }
  let localeValue;
  switch (locale) {
    case 'client':
      localeValue = getClientConfig().defaultLocale;
      break;
    case 'local':
      localeValue = window.navigator.language;
      break;
  }
  return formatDateTime(value, format, timeZoneValue, localeValue ?? 'en-US', invalid ?? t('unk'));
};

export default useDateTimeFormat;
